import { useCallback, useEffect, useRef } from 'react';
import { getOnboardingStep, getTotalOnboardingSteps } from '@/constants/onboarding';
import { logAnalyticsEvent } from '@/services/analyticsLogger';
import { getPendingNavigationIntent } from '@/navigation/pendingNavigationIntent';

type OnboardingSkipReason = 'user_skip' | 'already_completed' | 'not_required';
type OnboardingEntryPoint = 'standard' | 'template_deeplink' | 'invite_deeplink' | 'unknown';

const getOnboardingEntryPoint = (): OnboardingEntryPoint => {
  const intent = getPendingNavigationIntent();
  if (!intent) return 'standard';
  if (intent.kind === 'invite') return 'invite_deeplink';
  if (intent.kind === 'route' && intent.params?.templateId) return 'template_deeplink';
  return 'unknown';
};

export function useOnboardingStepAnalytics(step: string, enabled = true) {
  const viewedRef = useRef(false);
  const stepIndex = getOnboardingStep(step) ?? undefined;
  const totalSteps = getTotalOnboardingSteps();

  useEffect(() => {
    if (!enabled || viewedRef.current) return;
    viewedRef.current = true;
    void logAnalyticsEvent('onboarding_step_viewed', {
      step,
      stepIndex,
      totalSteps,
      entryPoint: getOnboardingEntryPoint(),
    });
  }, [enabled, step, stepIndex, totalSteps]);

  const logCompleted = useCallback(() => {
    if (!enabled) return;
    void logAnalyticsEvent('onboarding_step_completed', {
      step,
      stepIndex,
      totalSteps,
      entryPoint: getOnboardingEntryPoint(),
    });
  }, [enabled, step, stepIndex, totalSteps]);

  const logSkipped = useCallback(
    (reason: OnboardingSkipReason = 'user_skip') => {
      if (!enabled) return;
      void logAnalyticsEvent('onboarding_step_skipped', {
        step,
        stepIndex,
        totalSteps,
        reason,
      });
    },
    [enabled, step, stepIndex, totalSteps],
  );

  return { logCompleted, logSkipped };
}
